/**
 * loggerProxy — logging for shared/ code, which must not know whether it is
 * running in Node or a browser.
 *
 * Each side installs its logger once at startup:
 *   server:  setSharedLoggerFactory((category) => LoggerFactory.get(LOG_CATEGORIES[category] || LOG_CATEGORIES.GENERAL))
 *   client:  setSharedLoggerFactory((category) => ClientLogger.getLogger(category.toLowerCase()))
 * Until then (and in tests that never install one) calls fall back to console.
 *
 * getSharedLogger() returns a proxy, so a module may grab its logger at import
 * time and still pick up a factory installed later.
 */
let factory = null;

const noop = () => {};
const consoleFallback = {
  info: (...a) => console.info(...a),
  warn: (...a) => console.warn(...a),
  error: (...a) => console.error(...a),
  debug: typeof console.debug === 'function' ? (...a) => console.debug(...a) : noop,
  log: (...a) => console.log(...a)
};

function resolveCategoryKey(categoryKey) {
  if (!categoryKey || typeof categoryKey !== 'string') return 'GENERAL';
  return categoryKey.toUpperCase();
}

/** @param {((category: string) => object) | null} fn */
export function setSharedLoggerFactory(fn) {
  factory = typeof fn === 'function' ? fn : null;
}

export function getSharedLogger(categoryKey = 'GENERAL') {
  const category = resolveCategoryKey(categoryKey);
  const resolve = () => (factory ? factory(category) : null) || consoleFallback;
  return {
    info: (...a) => resolve().info(...a),
    warn: (...a) => resolve().warn(...a),
    error: (...a) => resolve().error(...a),
    debug: (...a) => (resolve().debug || noop)(...a),
    log: (...a) => (resolve().log || resolve().info)(...a)
  };
}
